import { GroupInfo } from '@moovly/clients/lib/resources/group/group.interfaces';
import { Organization } from '@moovly/clients/lib/resources/organization/organization.interface';
import { UserProfile } from '@moovly/clients/lib/resources/users/users.interfaces';
import React from 'react';
import { useSelector } from 'react-redux';
import { RequestStatus } from '../config/store';
import { RootState } from '../store/rootStore';
import { License } from '@moovly/clients/lib/resources/licenses/licenses.interfaces';

export interface CurrentUserInfo {
  user: UserProfile | null;
  license: License | null;
  isGroupAdmin: boolean;
  isOrganizationAdmin: boolean;
  groupsWithAdminRight: GroupInfo[];
  isPartOfOrganization: boolean;
  parentOrganizationId: null | string;
  isDone: boolean;
  organizationWithAdminRight: Organization | null;
}

const useCurrentUser = (): CurrentUserInfo => {
  const currentUser = useSelector((state: RootState) => state.global.user.data);
  const license = useSelector((state: RootState) => state.global.license.data);

  const groups = useSelector((state: RootState) => state.accesss.organization.data?.groups);
  const organizationInfo = useSelector((state: RootState) => state.accesss.organization.data?.info);
  const isDone = useSelector(
    (state: RootState) => state.accesss.organization.request.get.status === RequestStatus.Success
  );

  const isOrganizationAdmin = React.useMemo(() => {
    return !!(isDone && organizationInfo && organizationInfo.admins.includes(currentUser?.uuid!));
  }, [organizationInfo, currentUser, isDone]);
  const groupsWithAdminRight = React.useMemo(() => {
    if (isOrganizationAdmin) {
      return [...organizationInfo!.groups].sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );
    }
    if (!currentUser || !groups || !isDone) {
      return [];
    }
    return groups?.filter((group) => group.admins.includes(currentUser?.uuid));
  }, [currentUser, isOrganizationAdmin, organizationInfo, groups, isDone]);

  return {
    user: currentUser,
    license,
    isGroupAdmin: !!groupsWithAdminRight.length,
    isOrganizationAdmin,
    groupsWithAdminRight,
    isDone,
    isPartOfOrganization: (isDone && !!organizationInfo) || false,
    parentOrganizationId: (organizationInfo && organizationInfo.id) || null,
    organizationWithAdminRight: isOrganizationAdmin ? organizationInfo! : null,
  };
};

export default useCurrentUser;
